import React, { useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { CleryBioPersonInfo, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { ClergyBioPersonalInfoFormModel, religiousOrder } from './model/form-models';
import { FormField } from '../../../global/components/form-element/form-field';
import { AddressField } from '../../../global/components/form-element/address';
import { getAddressLabels } from '../../account-info/selectors/form-selectors';
import { clergyBioAddressFieldNames, FormTypes } from '../../account-info/models/form-models';
import { ClergyBioPersonalInfoMailingAddressRequiredSchema } from '../../account-info/features/personal-info/personal-form-models';
import { ApiStatuses } from '../../../global/models/api-model';
import { patchClergyBioPersonInfo } from '../../../global/ducks/clergy-bio-api';
import { ConstructPayload, GetClergyBioPersonUpdateValidationSchema } from './clergy-bio-person-update-helpers';
import { setClergyBioPersonalInfo } from '../clergy-bio-slice';
import EcdMessage from '../summary/ecd-message';
import { FormGroup } from '../../../global/components/layout/form-group';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';
import { apiErrorClear } from '../../../global/ducks/base-api';

const personalWebSiteKey = 'personalWebsite';

function ClergyBioPersonalInfoUpdate() {

	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.personInfo);
	const personalInfo = useSelector<TcsmStates, CleryBioPersonInfo>(state => state?.clergyBioState?.personInfo);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const dispatch = useDispatch();
	const initialValues: ClergyBioPersonalInfoFormModel = {
		firstName: personalInfo?.personal?.details?.firstNamePub,
		lastName: personalInfo?.personal?.details?.lastNameAtBirth,
		middleName: personalInfo?.personal?.details?.middleNamePub,
		placeOfBirth: personalInfo?.personal?.details?.placeOfBirth,
		nameAppear: `${personalInfo?.personal?.details?.firstNamePub || personalInfo?.personal?.details?.firstName} ${personalInfo?.personal?.details?.middleNamePub || personalInfo?.personal?.details?.middleName} ${personalInfo?.personal?.details?.lastName}`,
		religiousOrder: personalInfo?.personal?.details?.religiousOrder,
		personalWebsite: personalInfo?.personal?.details?.personalWebsite?.url,
		country: personalInfo?.publishedAddress?.details.country,
		address1: personalInfo?.publishedAddress?.details.address1,
		address2: personalInfo?.publishedAddress?.details.address2,
		city: personalInfo?.publishedAddress?.details.city,
		state: personalInfo?.publishedAddress?.details.state,
		postalCode: personalInfo?.publishedAddress?.details.postalCode,
		clergyInfoConfirmed: false,
	};
	const validationSchema = GetClergyBioPersonUpdateValidationSchema(labelSettings, settings, personalInfo?.isNewClergy);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const [errorMaps, setErrorMaps] = useState<any>(null);

	const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Professional, guid);
			setUpdateStatus(ApiStatuses.Success);
			dispatch(setClergyBioPersonalInfo(res?.data));
		}
		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);
			const responseErrors = res.data.publishedAddress?.details?.errors;
			const keys = Object.keys(responseErrors);
			const errors = {};
			for (let i = 0; i < keys.length; i += 1) {
				const indexedError = responseErrors[keys[i]];
				errors[keys[i]] = indexedError && indexedError[0];
			}
			errors[personalWebSiteKey] = res.data.personal?.details?.errors?.personalWebsite?.url && res.data.personal?.details?.errors?.personalWebsite?.url[0];
			setErrorMaps(errors);
		}
	};

	const updatePerson = (values: ClergyBioPersonalInfoFormModel) => {
		setErrorMaps(null);
		dispatch(apiErrorClear());
		dispatch(patchClergyBioPersonInfo(guid, ConstructPayload(values), onSuccess));
	};

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updatePerson(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={errorMaps}
			>
				{(formProps: FormikProps<ClergyBioPersonalInfoFormModel>) => (

					<Form>
						{
							updateStatus === ApiStatuses.Failure &&
							<ContentSpacer>
								<Banner
									banner={{
										type: 'error',
										label: <p><strong>{errorMessage}</strong></p>,
									}}
								/>
							</ContentSpacer>
						}
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								window.location.reload();
							}}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_PERSON_UPDATE] : []}
						>
							<ContentSpacer>
								<EcdMessage />
							</ContentSpacer>

							<DividerSection sectionText={settings?.personalUpdateHeader} sectionDescription={settings?.personalUpdateDescription} showCleryIcon >

								<FormGroup>
									{
										personalInfo?.isNewClergy &&
										<FormField
											name='clergyInfoConfirmed'
											label={settings?.confirmationCheckBoxText}
											component='checkBox'
											touched={formProps.touched}
											errors={formProps.errors}
											value={formProps?.values?.clergyInfoConfirmed ?? false}
											checked={formProps?.values?.clergyInfoConfirmed ?? false}
										/>
									}
									<FormField
										name='firstName'
										label={settings.firstNameLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
									/>
									<FormField
										name='middleName'
										label={settings.middleNameLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
									/>
									<FormField
										name='lastName'
										label={settings.lastNameLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
									/>
									<FormField
										name='placeOfBirth'
										label={settings.placeOfBirthLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
									/>
									<FormField
										name='nameAppear'
										label={settings.nameAppearAsFormLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
										disabled
									/>
									<FormField
										name='religiousOrder'
										label={settings.religiousOrderLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
										select
										options={religiousOrder}
									/>
									<FormField
										name='personalWebsite'
										label={settings.personUpdateWebsiteLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
									/>
								</FormGroup>
							</DividerSection>
							<DividerSection removeDivider sectionText={settings?.publishedAddressHeader} sectionDescription={settings?.publishedAddressSectionDescription} showCleryIcon >
								<AddressField
									currentCountry={formProps.values.country === '' ? 'US' : formProps.values.country}
									labels={getAddressLabels(labelSettings)}
									addressFieldNames={clergyBioAddressFieldNames}
									formProps={formProps}
									requiredSchema={ClergyBioPersonalInfoMailingAddressRequiredSchema}
									person
								/>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<>
			<PageHeader headerText={settings?.personalTitle} />
			<ClergyBioComponentLoader
				state={loadingState}
				content={renderContent()}
			/>

		</>
	);
}


export default ClergyBioPersonalInfoUpdate;